import axios from 'axios';

const STORAGE_ACCESS = 'starter_access';
const STORAGE_REFRESH = 'starter_refresh';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const API_PREFIX = '/api';
const IS_DEV_RUNTIME = Boolean(import.meta.env.DEV || import.meta.env.MODE === 'test');
const ABSOLUTE_URL_RE = /^https?:\/\//i;

export function normalizeApiBaseUrl(baseUrl) {
  const raw = String(baseUrl ?? '').trim();
  if (!raw) return API_PREFIX;
  if (ABSOLUTE_URL_RE.test(raw)) {
    const normalized = raw.replace(/\/+$/, '');
    if (typeof window !== 'undefined') {
      try {
        const target = new URL(normalized);
        const current = window.location.hostname;
        const localHosts = new Set(['localhost', '127.0.0.1']);
        const isLocalMismatch = localHosts.has(target.hostname) && target.hostname !== current;
        if (isLocalMismatch) {
          return normalized.endsWith(API_PREFIX) ? normalized : `${normalized}${API_PREFIX}`;
        }
      } catch {
        // fallback to normalized absolute URL below
      }
    }
    return normalized.endsWith(API_PREFIX) ? normalized : `${normalized}${API_PREFIX}`;
  }

  let normalized = raw.startsWith('/') ? raw : `/${raw}`;
  normalized = normalized.replace(/\/+$/, '');
  return normalized === '' ? API_PREFIX : normalized;
}

const NORMALIZED_API_BASE_URL = normalizeApiBaseUrl(API_BASE_URL);

const api = axios.create({
  baseURL: NORMALIZED_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
  timeout: 15000,
});
const refreshApi = axios.create({
  baseURL: NORMALIZED_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
  timeout: 15000,
});

let onUnauthorized = null;
let onApiError = null;

export function setTokens({ access, refresh }) {
  if (access) localStorage.setItem(STORAGE_ACCESS, access);
  else localStorage.removeItem(STORAGE_ACCESS);

  if (refresh) localStorage.setItem(STORAGE_REFRESH, refresh);
  else localStorage.removeItem(STORAGE_REFRESH);
}

export function loadPersistedTokens() {
  return {
    access: localStorage.getItem(STORAGE_ACCESS),
    refresh: localStorage.getItem(STORAGE_REFRESH),
  };
}

export function clearTokens() {
  setTokens({ access: null, refresh: null });
}

export function onAuthFailure(callback) {
  onUnauthorized = callback;
}

export function onApiErrorEvent(callback) {
  onApiError = callback;
  return () => {
    if (onApiError === callback) onApiError = null;
  };
}

let refreshingPromise = null;

export function normalizeApiPath(path) {
  const raw = String(path ?? '').trim();
  if (!raw) return '/';
  if (ABSOLUTE_URL_RE.test(raw)) return raw;

  let normalized = raw.startsWith('/') ? raw : `/${raw}`;
  while (normalized.includes('//')) {
    normalized = normalized.replace('//', '/');
  }
  if (normalized.startsWith(`${API_PREFIX}/`)) {
    normalized = normalized.slice(API_PREFIX.length);
  } else if (normalized === API_PREFIX) {
    normalized = '/';
  }
  return normalized;
}

function reportApiAnomaly(message, details) {
  if (!IS_DEV_RUNTIME || typeof window === 'undefined') return;
  // eslint-disable-next-line no-console
  console.warn(`[api-guard] ${message}`, details);
  window.dispatchEvent(new CustomEvent('starter:api-warning', {
    detail: { source: 'api-guard', message, details },
  }));
}

function validateRequestTarget(rawUrl) {
  const url = String(rawUrl ?? '');
  if (!url) return;

  if (url.includes('/api/api/')) {
    reportApiAnomaly('Double API prefix detected in request path.', { url });
  }

  if (ABSOLUTE_URL_RE.test(url)) {
    let target;
    try {
      target = new URL(url);
    } catch {
      if (IS_DEV_RUNTIME) {
        reportApiAnomaly('Malformed absolute URL in request.', { url });
      }
      throw new Error('Malformed absolute URL in API request.');
    }
    const allowedHost = window.location.hostname;
    let configuredHost = allowedHost;
    if (ABSOLUTE_URL_RE.test(NORMALIZED_API_BASE_URL)) {
      try {
        configuredHost = new URL(NORMALIZED_API_BASE_URL).hostname;
      } catch {
        configuredHost = allowedHost;
      }
    }
    const localHosts = new Set(['localhost', '127.0.0.1']);
    const hostMatchesCurrent = target.hostname === allowedHost;
    const hostMatchesConfigured = target.hostname === configuredHost;
    const localAliasMatch = localHosts.has(target.hostname) && localHosts.has(allowedHost);

    if (!hostMatchesCurrent && !hostMatchesConfigured && !localAliasMatch) {
      if (IS_DEV_RUNTIME) {
        reportApiAnomaly('Request targets an unexpected host.', { url, configuredHost, allowedHost });
      }
      throw new Error(`Unexpected API host: ${target.hostname}`);
    }
  }
}

function refreshTokenRequest() {
  if (!refreshingPromise) {
    const refresh = localStorage.getItem(STORAGE_REFRESH);
    refreshingPromise = refreshApi
      .post('/v1/auth/refresh', { refresh_token: refresh })
      .finally(() => {
        refreshingPromise = null;
      });
  }
  return refreshingPromise;
}

function isExpiredAccessTokenError(error) {
  const response = error?.response;
  if (!response || response.status !== 401) return false;

  const data = response.data ?? {};
  const headers = response.headers ?? {};
  const details = `${data?.code ?? ''} ${data?.message ?? ''} ${data?.error ?? ''} ${headers?.['www-authenticate'] ?? ''}`
    .toLowerCase();
  return details.includes('expired');
}

/* ===================== REQUEST INTERCEPTOR ===================== */

api.interceptors.request.use((config) => {
  const normalizedUrl = normalizeApiPath(config.url || '');
  config.url = normalizedUrl;
  validateRequestTarget(normalizedUrl);

  const isPublic = normalizedUrl.includes('/public/');
  const access = localStorage.getItem(STORAGE_ACCESS);

  if (access && !isPublic && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  if (!config.headers.Accept) {
    config.headers.Accept = 'application/json';
  }
  config.metadata = { startedAt: performance.now() };
  return config;
});

/* ===================== RESPONSE INTERCEPTOR ===================== */

const AUTH_ENDPOINTS = ['/v1/auth/refresh', '/v1/auth/login'];

api.interceptors.response.use(
  (response) => {
    const startedAt = response?.config?.metadata?.startedAt;
    if (typeof startedAt === 'number') {
      const durationMs = Math.round(performance.now() - startedAt);
      window.dispatchEvent(new CustomEvent('starter:api-metric', {
        detail: {
          method: (response.config?.method || 'get').toUpperCase(),
          url: response.config?.url || '',
          status: response.status,
          durationMs,
        },
      }));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const retryAfter = Number(error?.response?.headers?.['retry-after'] ?? 0);
    if (error?.response && Number.isFinite(retryAfter) && retryAfter > 0) {
      error.retryAfter = retryAfter;
    }
    if (onApiError) {
      onApiError(error);
    }

    if (!error.response) {
      return Promise.reject(error);
    }
    if (error.response.status !== 401) {
      return Promise.reject(error);
    }
    if (!originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }
    const url = String(originalRequest.url || '');
    if (AUTH_ENDPOINTS.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    if (url.includes('/public/')) {
      return Promise.reject(error);
    }
    if (!isExpiredAccessTokenError(error)) {
      return Promise.reject(error);
    }

    const refresh = localStorage.getItem(STORAGE_REFRESH);
    if (!refresh) {
      clearTokens();
      if (onUnauthorized) onUnauthorized();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const { data } = await refreshTokenRequest();

      const newAccessToken =
        data?.token ||
        data?.access ||
        data?.access_token;

      const newRefreshToken = data?.refresh_token || refresh;

      if (!newAccessToken) {
        throw new Error('No access token returned from refresh endpoint');
      }

      setTokens({
        access: newAccessToken,
        refresh: newRefreshToken,
      });

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${newAccessToken}`,
      };

      return api(originalRequest);
    } catch (err) {
      clearTokens();
      if (onUnauthorized) onUnauthorized();
      return Promise.reject(err);
    }
  }
);

/**
 * Requête annulée (AbortController, navigation, StrictMode).
 * @param {unknown} error
 */
export function isRequestAborted(error) {
  if (!error || typeof error !== 'object') return false;
  const e = /** @type {{ code?: string, name?: string }} */ (error);
  return (
    e.code === 'ERR_CANCELED' ||
    e.name === 'CanceledError' ||
    e.name === 'AbortError' ||
    axios.isCancel?.(error) === true
  );
}

export { api };
export default api;
